import React, { useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { GCSB_CREDITS } from "@/data/credits";

const PGDCSL_FAQS = [
  ["What is the PGDCSL?", "The Postgraduate Executive Diploma in Converged Security Leadership is an independent professional qualification in converged security governance, executive leadership, enterprise risk and organisational resilience. It is not a university degree."],
  ["Who controls the qualification and who teaches it?", "The programme and its rules are controlled by the Global Converged Security Board (GCSB). The Converged Security Institute (CSI) is the education provider that delivers the learning, study materials and support."],
  ["What does the pathway include?", "Five components: CSCE (Converged Security Cyber Expert), CSP (Converged Security Professional), CSL (Chartered Converged Security Leader), CCSO (Chief Converged Security Officer) and a final Executive Integration Portfolio of 3,000-5,000 words."],
  ["How long does it take?", "Most candidates plan for 24-36 months. Your registration stays valid for five years, so you can study at a pace that fits your work."],
  ["I'm an experienced executive. Can my experience count?", "Yes. With at least 15 years of relevant experience and senior leadership evidence, you can apply for executive entry. An independent panel reviews your CV, references and a short evidence portfolio. The final Executive Integration Portfolio is always completed."],
  ["How is the final portfolio assessed?", "It is marked out of 100 with a pass mark of 70, by a lead assessor and an independent moderator. Results can be Distinction, Merit or Pass."],
  ["How many credits is it worth?", "90 GCSB Academic Credits across the five components. These are professional programme credits, not university credits unless recognised through a formal articulation agreement."],
  ["What happens after I graduate?", "You keep your qualification up to date with 25 hours of CPD a year, submitted online at gcsb.uk/cpd."],
];

const eur = (n) => `€${Number(n).toLocaleString("en-IE", { maximumFractionDigits: 2 })}`;
const CERTS = {
  CSCE: { name: "Converged Security Cyber Expert", step: "the first step", cpd: 10, credits: GCSB_CREDITS.CSCE, delivery: "live online with CSI faculty in small cohorts", next: "CSP" },
  CSP: { name: "Converged Security Professional", step: "the second step", cpd: 15, credits: GCSB_CREDITS.CSP, delivery: "in an instructor-led live online cohort with tabletop exercises", next: "CSL" },
};

const certFaqs = (code, price, examOnly) => {
  const c = CERTS[code];
  return [
    [`What is the ${code}?`, `The ${c.name} (${code}) certification is ${c.step} of the CSI pathway towards the PGDCSL Postgraduate Executive Diploma. It recognises practical knowledge of converged cyber, physical and operational technology security.`],
    examOnly
      ? [`What does ${code} Exam Only include?`, `For ${eur(price)} you get self-learning access, the official study guides, the certification examination and two examination attempts. It suits professionals who prefer to prepare on their own.`]
      : [`How is the ${code} course delivered?`, `The course is taught ${c.delivery}, followed by the certification examination. It costs ${eur(price)}. If you prefer to study on your own, ${code} Exam Only is also available.`],
    ["What is the exam like?", "A 3-hour online examination. The pass mark is 70%, and you can download your result report afterwards."],
    ["What if I don't pass first time?", "Two examination attempts are included. If both are unsuccessful, you can book a resit."],
    ...(examOnly ? [["Is there a discount?", "Yes. If a CSI Ambassador referred you, enter their code at checkout for 5% off."]] : []),
    ["How can employers check my certificate?", "Every certificate can be checked online on the CSI Verify page."],
    ["Does it count towards the PGDCSL?", `Yes. ${code} carries ${c.credits} GCSB Academic Credits towards the ${GCSB_CREDITS.PGDCSL} of the complete PGDCSL. The next step is ${c.next}.`],
    ["Do I need to keep it up to date?", `Yes, through ${c.cpd} hours of CPD a year, submitted online at gcsb.uk/cpd.`],
  ];
};

export const faqsFor = (course) => {
  const code = course?.code || "";
  if (code === "PGDCSL") return PGDCSL_FAQS;
  const base = code.replace("-EXAM", "");
  return CERTS[base] ? certFaqs(base, course.price, code.endsWith("-EXAM")) : null;
};

export const ProgrammeFaq = ({ course, className = "" }) => {
  const FAQS = faqsFor(course);
  const label = course.code.replace("-EXAM", " Exam Only");
  useEffect(() => {
    if (!FAQS) return undefined;
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.dataset.seo = "faq";
    s.text = JSON.stringify({
      "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: FAQS.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
    });
    document.head.appendChild(s);
    return () => s.remove();
  }, [course.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!FAQS) return null;
  return (
    <section className={`card-warm p-5 sm:p-8 ${className}`} aria-labelledby="programme-faq-h" data-testid="programme-faq">
      <div className="eyebrow mb-1">{label}</div>
      <h2 id="programme-faq-h" className="font-display text-2xl sm:text-3xl mb-4">Frequently asked questions</h2>
      <div className="divide-y divide-[var(--border-warm)]">
        {FAQS.map(([q, a], i) => (
          <details key={q} className="group py-3" data-testid={`programme-faq-${i}`}>
            <summary className="flex items-center justify-between gap-3 cursor-pointer list-none font-semibold text-[var(--navy)] text-sm sm:text-base rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gold-deep)]">
              {q}<ChevronDown size={16} className="shrink-0 transition-transform group-open:rotate-180" aria-hidden="true"/>
            </summary>
            <p className="text-sm text-zinc-700 leading-relaxed mt-2">{a}</p>
          </details>
        ))}
      </div>
    </section>
  );
};
